import React from "react";
import {
  AccountTypeEnum,
  ComplianceObjectType,
  ProfileRelationshipEnum,
  RiskRating,
} from "../../../../../graphql/supergraphEnums";
import {
  calculateOwnershipShares,
  getRelationshipLabel,
  provideLegalName,
} from "../../../../ProfileTree/utils";
import { shouldShowRiskRating } from "../../../../riskRating/utils";
import { HierarchyChildProfile, HierarchyRootProfile } from "../types";
import { PositionNodeCard } from ".";
import type { PositionNodeData, PositionHierarchyNode } from "./types";
import type { Node } from "../../../../common/TreeComponent/types";

export const buildTreeNode = (node: PositionNodeData, parentId?: string): Node => ({
  id: node.id,
  content: <PositionNodeCard node={node} />,
  parentId,
  edgeLabel: node.ownershipPct != null ? `${node.ownershipPct}%` : undefined,
  rootEdgeLabel: node.rootOwnershipPct != null ? `${node.rootOwnershipPct}%` : undefined,
  leafNodes: node.children?.map((child) => buildTreeNode(child, node.id)) ?? [],
});

export const buildPositionHierarchy = (
  investingProfile: HierarchyRootProfile,
  profiles: HierarchyChildProfile[],
): PositionHierarchyNode => {
  const rootNode: PositionHierarchyNode = {
    id: investingProfile.id,
    profileInfo: investingProfile,
    children: [],
  };

  const nodeMap = new Map<number, PositionHierarchyNode>();
  nodeMap.set(rootNode.id, rootNode);

  for (const profile of profiles) {
    if (!profile) continue;
    const percentageOwnership = profile?.parentRelationships?.find(
      (pr) => pr.relationshipType === ProfileRelationshipEnum.Owner,
    )?.percentOwnership;

    nodeMap.set(profile.id, {
      id: profile.id,
      parentId: profile.parentRelationships[0]?.parentProfileId,
      percentageOwnership: percentageOwnership ?? null,
      profileInfo: profile,
      children: [],
    });
  }

  for (const node of nodeMap.values()) {
    if (node.parentId) {
      nodeMap.get(node.parentId)?.children.push(node);
    }
  }

  calculateOwnershipShares(rootNode.children);

  return rootNode;
};

export const toHierarchyNodeData = (
  node: PositionHierarchyNode,
  programId?: number,
): PositionNodeData => {
  const { riskRating, parentRelationships, complianceObjectType } = node.profileInfo;
  const isRootProfile = !node.parentId;
  let accountType = node.profileInfo.accountType;
  if (!accountType && complianceObjectType === ComplianceObjectType.Individual) {
    accountType = AccountTypeEnum.Individual;
  }

  const rawRiskStatus =
    riskRating?.overriddenRating ?? riskRating?.calculatedRating ?? RiskRating.None;

  return {
    id: node.id.toString(),
    name: provideLegalName(node.profileInfo),
    roles: isRootProfile
      ? ["Subscriber account"]
      : (parentRelationships ?? [])
          .map((pr) => getRelationshipLabel(pr.relationshipType))
          .filter((label): label is string => Boolean(label)),
    accountType: accountType,
    riskStatus: shouldShowRiskRating(programId) ? rawRiskStatus : RiskRating.None,
    ownershipPct: node.percentageOwnership,
    rootOwnershipPct: node.rootPercentageOwnership,
    children: (node.children ?? []).map((child) => toHierarchyNodeData(child, programId)),
  };
};
